import type { LucideIcon } from "lucide-react";

import { CLUSTER_META, clusterRoute, presentationOf } from "@/entities/capability";
import type { CapabilityCluster } from "@/entities/capability";
import type { ClusterCards } from "@/pages/workbench/useClusterCards";
import { isNavItemActive, type NavItem } from "@/widgets/appSidebarNav";

import { RAIL_SECTIONS, type RailKey } from "./shellNav";

/** 只取 t 用得到的那一小撮签名 —— 本模块要保持无 React / 无 i18next 运行时依赖(可裸单测)。 */
export type StripT = (key: string, options?: { defaultValue?: string }) => string;

export interface StripItem {
  key: string;
  label: string;
  /** 站内落点(走 SPA)。与 externalUrl 互斥。 */
  route?: string;
  /** 外站落点(新开标签页,如漫剧工坊)。与 route 互斥。 */
  externalUrl?: string;
  icon: LucideIcon;
  active: boolean;
}

export interface StripCrumb {
  label: string;
  /** 能力群 = 群页(可点回);支撑区无「区首页」→ undefined,不可点。 */
  route?: string;
}

export interface StripModel {
  /** cluster 的空 items 要显示「舱即将开放」,support 的空 items 直接不渲染 —— 故 kind 要透出。 */
  kind: "cluster" | "support";
  crumb: StripCrumb;
  items: StripItem[];
}

export interface StripInput {
  railKey: RailKey;
  pathname: string;
  t: StripT;
  /** 能力群轨:`useClusterCards().forCluster(c)` 的产物(与首页/群页/⌘K 同一单源);非群轨传 null。 */
  cards: ClusterCards | null;
  /** 支撑区轨:已过 `visibleByPerms` 的 NavItem;非支撑轨传空数组。 */
  support: NavItem[];
}

/**
 * 顶栏中段「当前轨项的下一层」的**纯数据解析**(2026-09-17 壳导航重排)。
 *
 * ★为什么要有它:左轨(74px)选的是「哪一群 / 哪一区」,而顶栏中段此前是 0817 把左轨**同一层**
 *   镜像上去的一排群链接 —— 两处画同一层,用户读到的是重复而不是层级。改成显示下一层:
 *   群 → 该群已开舱;支撑区 → 该区页面;首页 → 空(首页本身就是全景,不需要再指路)。
 * ★舱的来源**不另起一套**:depts / caps 由调用方从 `useClusterCards` 取,门控口径与首页卡墙、
 *   群页、⌘K 逐字同源。这里只做「哪些能上条 + 谁高亮 + 叫什么」。
 * ★占位舱(coming_soon / integrating)与「active 但 presentation 没落点」的舱都不上条:
 *   条上每一格都是能点进去的地方,不给点不动的假承诺(与 ⌘K 命令表同一条口径)。
 */
export function buildStrip({
  railKey,
  pathname,
  t,
  cards,
  support,
}: StripInput): StripModel | null {
  if (railKey === "home") return null;

  const section = RAIL_SECTIONS.find((s) => s.key === railKey);
  if (!section) return null;

  if (section.kind === "cluster") {
    const cluster = railKey as CapabilityCluster;
    const items: StripItem[] = [];

    // 内容群 5 个部门舱:全部上条(它们恒有 route,门控在 useClusterCards 里已经做过)。
    for (const d of cards?.depts ?? []) {
      items.push({
        key: `dept-${d.moduleKey}`,
        label: d.titleKey ? t(d.titleKey, { defaultValue: d.title }) : d.title,
        route: d.route,
        icon: d.icon,
        active: isNavItemActive(pathname, d.route),
      });
    }

    // 能力卡:只有「已开 且 有落点」的才是一格可点的舱。
    for (const c of cards?.caps ?? []) {
      const pres = presentationOf(c.capability);
      if (!pres || c.status !== "active") continue;
      if (!pres.route && !pres.externalUrl) continue;
      items.push({
        key: `cap-${c.capability}`,
        label: t(`cabin.name.${c.capability}`, { defaultValue: c.displayName }),
        route: pres.route,
        externalUrl: pres.route ? undefined : pres.externalUrl,
        icon: pres.icon,
        // 外站舱不在 SPA 路由里,永远不会是「当前页」。
        active: pres.route ? isNavItemActive(pathname, pres.route) : false,
      });
    }

    return {
      kind: "cluster",
      crumb: { label: t(CLUSTER_META[cluster].labelKey), route: clusterRoute(cluster) },
      items,
    };
  }

  // 支撑区:一项都不剩(权限全过滤掉)时没有下一层可显示。
  if (support.length === 0) return null;
  return {
    kind: "support",
    crumb: { label: t(section.labelKey) },
    items: support.map((it) => ({
      key: it.url,
      label: it.titleKey ? t(it.titleKey, { defaultValue: it.title }) : it.title,
      route: it.url,
      icon: it.icon,
      active: isNavItemActive(pathname, it.url),
    })),
  };
}
